import type { NotificationSetting } from '@/services/notificationSettingsService';
import {
  MANAGED_NOTIFICATION_TYPES,
  NOTIFICATION_RECIPIENT_CONTRACT,
  contractAllowsKind,
  getNotificationRecipientContract,
  type NotificationRecipientContractEntry,
  type RecipientTargetKind
} from '../../../../supabase/functions/_shared/notification-recipient-contract';
import {
  supportedEncompassRoleById,
  supportedEncompassRoleForLegacySlug
} from '../../../../supabase/functions/_shared/encompass-role-contract';
import { RESENDABLE_NOTIFICATION_TYPES } from '../../../../supabase/functions/_shared/my-email-notifications-catalog';
import { QUOTE_FAILURE_MAX_CC } from '../../../../supabase/functions/_shared/consumer-quote-failure-email';
import {
  isRecipientConfigV1,
  isSameTarget,
  type RecipientConfigV1,
  type RecipientTarget,
  type TemplateOverrides
} from './types';

export const SEND_TEST_DISABLED_TYPES = new Set([
  'consumer_board_health',
  'marketing_market_brief_completed',
  'marketing_request_lo_update',
  'marketing_request_lo_reminder',
  'marketing_request_director_alert',
  'marketing_request_director_digest',
  'calendar_booking_update',
  'artemis_operational_health',
  'clarifii_reliance_letter',
  'google_review_request',
  'google_review_request_realtor',
  'clean_approval_ready',
  'lo_volume_bonus_agreement',
  'marketing_lo_stall_digest',
  'appraisal_payment_link_reminder',
  'rate_lock_confirmation_critical'
]);

/**
 * Mandatory notification types: the sender ignores is_enabled, the database keeps it true, and
 * Email Notifications shows "Always on" instead of a switch.
 */
export const ALWAYS_ON_NOTIFICATION_TYPES = new Set(['rate_lock_confirmation_critical']);

/**
 * Notification types whose Activity rows offer a "Resend" of one earlier email. Derived from
 * the shared My Email Notifications catalog so admin Activity, the user-side page and
 * send-review-request all agree on one list.
 */
export const ACTIVITY_RESENDABLE_TYPES = new Set<string>([...RESENDABLE_NOTIFICATION_TYPES, 'recruit_sign_in_verification']);

export const LIFECYCLE_EDITABLE_TYPES = new Set([
  'preapproval_requested',
  'preapproval_submitted_underwriting',
  'preapproval_issued',
  'le_due_disclosures_required',
  'initial_disclosures_sent',
  'intent_to_proceed_received',
  'intent_to_proceed_outstanding',
  'flood_zone_identified',
  'file_submitted_processing',
  'file_submitted_underwriting',
  'file_suspended_underwriting',
  'final_approval_issued',
  'docs_out',
  'closing_disclosure_sent',
  'redisclosure_required',
  'loan_funded',
  'loan_purchased',
  'rate_lock_expired',
  'mi_activation_required',
  'pricing_field_changed',
  'price_finalized'
]);

export const DISPLAY_ONLY_NOTIFICATION_TYPES = new Set<string>([
  ...MANAGED_NOTIFICATION_TYPES.filter(
    (type) => NOTIFICATION_RECIPIENT_CONTRACT[type].routingMode === 'display_only'
  )
]);

export const SETTINGS_PRIMARY_TYPES = new Set<string>([
  'servicing_goodbye_letter_failed',
  ...MANAGED_NOTIFICATION_TYPES.filter(
    (type) => NOTIFICATION_RECIPIENT_CONTRACT[type].routingMode === 'settings_primary'
  )
]);

export const APPEND_ONLY_NOTIFICATION_TYPES = new Set<string>([
  ...MANAGED_NOTIFICATION_TYPES.filter(
    (type) => NOTIFICATION_RECIPIENT_CONTRACT[type].routingMode === 'required_plus_additions'
  )
]);

// TO routing is code-owned (assigned LO, resolved by the sender); CC accepts internal
// user / user_role only — NOT raw_email (unlike append-only). Mirrors the dedicated
// special-case in validate_notification_settings_recipient_config() for this type.
export const INTERNAL_CC_ONLY_TYPES = new Set([
  'disclosure_form_sent_to_lo'
]);

export const ADAPTER_MANAGED_RECIPIENT_TYPES = new Set([
  ...LIFECYCLE_EDITABLE_TYPES,
  'doc_order_submitted',
  'doc_order_sent_to_lo',
  'doc_order_lc_escalation',
  'doc_order_changes_requested',
  'doc_order_lo_signed',
  'google_review_request',
  'title_escrow_portal_invitation'
]);

export const RECIPIENT_EDITABLE_TYPES = new Set<string>([
  ...MANAGED_NOTIFICATION_TYPES.filter(
    (type) => NOTIFICATION_RECIPIENT_CONTRACT[type].routingMode !== 'display_only'
      && (
        NOTIFICATION_RECIPIENT_CONTRACT[type].allowedToKinds.length > 0
        || NOTIFICATION_RECIPIENT_CONTRACT[type].allowedCcKinds.length > 0
      )
  )
]);

export const ACTIONABLE_NOTIFICATION_TYPES = new Set<string>([
  ...RECIPIENT_EDITABLE_TYPES
]);

export const TEMPLATE_EDITABLE_TYPES = new Set([
  'recruit_sign_in_verification',
  'marketing_market_brief_completed',
  'marketing_request_lo_update',
  'marketing_request_lo_reminder',
  'marketing_request_director_alert',
  'marketing_request_director_digest',
  'calendar_booking_update',
  'artemis_operational_health',
  'invoice_fee_overage',
  'invoice_fee_review',
  'invoice_fee_daily_digest',
  'preapproval_requested',
  'preapproval_submitted_underwriting',
  'preapproval_issued',
  'intent_to_proceed_received',
  'intent_to_proceed_outstanding',
  'flood_zone_identified',
  'file_submitted_processing',
  'file_submitted_underwriting',
  'loan_funded'
]);

export const ADDITIONAL_CC_ONLY_TYPES = new Set<string>([
  ...MANAGED_NOTIFICATION_TYPES.filter((type) => {
    const contract = NOTIFICATION_RECIPIENT_CONTRACT[type];
    return contract.routingMode === 'required_plus_additions'
      && contract.allowedToKinds.length === 0
      && contract.allowedCcKinds.length > 0;
  })
]);
export const CC_APPEND_ENABLED_TYPES = APPEND_ONLY_NOTIFICATION_TYPES;

export const TEMPLATE_OVERRIDE_ALLOWED_TOKENS: Record<string, string[]> = {
  recruit_sign_in_verification: [],
  marketing_market_brief_completed: ['weekOf', 'passLabel'],
  marketing_request_lo_update: ['title', 'hostName', 'count'],
  marketing_request_lo_reminder: ['date'],
  marketing_request_director_alert: ['title', 'loName'],
  marketing_request_director_digest: ['date'],
  calendar_booking_update: ['title', 'hostName'],
  artemis_operational_health: ['count', 'checkedAtLabel'],
  invoice_fee_overage: ['loanNumber', 'borrowerName', 'propertyAddress', 'feeFamily', 'invoiceNumber'],
  invoice_fee_review: ['loanNumber', 'borrowerName', 'propertyAddress', 'feeFamily', 'invoiceNumber'],
  invoice_fee_daily_digest: ['digestDateLabel', 'count', 'overageCount', 'reviewCount'],
  preapproval_requested: ['loanNumber', 'borrowerName', 'borrowerLastName', 'loanOfficerName', 'propertyAddress'],
  preapproval_submitted_underwriting: ['loanNumber', 'borrowerName', 'borrowerLastName', 'loanOfficerName', 'processorName'],
  preapproval_issued: ['loanNumber', 'borrowerName', 'borrowerLastName', 'loanOfficerName', 'approvalAmount'],
  intent_to_proceed_received: ['loanNumber', 'borrowerName', 'borrowerLastName', 'itpDate'],
  intent_to_proceed_outstanding: ['loanNumber', 'borrowerName', 'borrowerLastName', 'leSentDate', 'daysSinceLeSent'],
  flood_zone_identified: ['loanNumber', 'borrowerName', 'borrowerLastName', 'propertyAddress', 'floodIndicator'],
  file_submitted_processing: ['loanNumber', 'borrowerName', 'borrowerLastName', 'loanOfficerName', 'processorName'],
  file_submitted_underwriting: ['loanNumber', 'borrowerName', 'borrowerLastName', 'loanOfficerName', 'processorName'],
  loan_funded: ['loanNumber', 'borrowerName', 'borrowerLastName', 'loanAmount', 'fundedDate']
};

export const ADDITIONAL_ONLY_TYPES = new Set([
  'loan_purchased',
  'pricing_field_changed',
  'price_finalized',
  'mi_activation_required'
]);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function recipientContractFor(
  notificationType: string
): NotificationRecipientContractEntry | null {
  return getNotificationRecipientContract(notificationType);
}

export function hasLifecycleDefaultRouting(notificationType: string): boolean {
  return LIFECYCLE_EDITABLE_TYPES.has(notificationType) && !ADDITIONAL_ONLY_TYPES.has(notificationType);
}

export const LOAN_ROLE_LABELS: Record<Extract<RecipientTarget, { kind: 'loan_role' }>['role'], string> = {
  loan_officer: 'Loan officer',
  loan_officer_2: 'Loan officer 2',
  processor: 'Processor',
  loan_officer_assistant: 'LO assistant',
  closer: 'Closer',
  jr_processor: 'Jr processor'
};

export const LOAN_PARTY_LABELS: Record<Extract<RecipientTarget, { kind: 'loan_party' }>['party'], string> = {
  borrower: 'Borrower',
  co_borrower: 'Co-borrower',
  borrower_pair: 'Borrower pair'
};

export const LOAN_CONTACT_LABELS: Record<Extract<RecipientTarget, { kind: 'loan_contact' }>['contact'], string> = {
  buyer_realtor: 'Buyer realtor',
  escrow_officer: 'Escrow officer'
};

const STANDARD_LIFECYCLE_TO: RecipientTarget[] = [
  { kind: 'loan_role', role: 'loan_officer' }
];

const STANDARD_LIFECYCLE_CC: RecipientTarget[] = [
  { kind: 'loan_role', role: 'loan_officer_2' },
  { kind: 'loan_role', role: 'processor' },
  { kind: 'loan_role', role: 'loan_officer_assistant' }
];

export function lifecycleDefaultRecipientConfig(notificationType: string): RecipientConfigV1 | null {
  if (!hasLifecycleDefaultRouting(notificationType)) {
    return null;
  }

  if (notificationType === 'file_suspended_underwriting') {
    return {
      version: 1,
      to: [
        { kind: 'loan_role', role: 'processor' },
        { kind: 'loan_role', role: 'jr_processor' },
        { kind: 'loan_role', role: 'loan_officer' },
        { kind: 'loan_role', role: 'loan_officer_assistant' }
      ],
      cc: []
    };
  }

  const cc = [...STANDARD_LIFECYCLE_CC];
  if (notificationType === 'final_approval_issued' || notificationType === 'docs_out') {
    cc.push({ kind: 'loan_role', role: 'closer' });
  }

  return {
    version: 1,
    to: [...STANDARD_LIFECYCLE_TO],
    cc
  };
}

export function mergeLifecycleDefaultRouting(
  notificationType: string,
  config: RecipientConfigV1
): RecipientConfigV1 {
  const defaultConfig = lifecycleDefaultRecipientConfig(notificationType);
  if (!defaultConfig) {
    return config;
  }

  const to = uniqueTargets([...defaultConfig.to, ...config.to]);
  const cc = uniqueTargets([...defaultConfig.cc, ...config.cc])
    .filter((target) => !to.some((toTarget) => isSameTarget(toTarget, target)));

  return {
    version: 1,
    to,
    cc
  };
}

export function canEditRecipients(notificationType: string): boolean {
  return RECIPIENT_EDITABLE_TYPES.has(notificationType) && !DISPLAY_ONLY_NOTIFICATION_TYPES.has(notificationType);
}

export function canEditTemplate(notificationType: string): boolean {
  return TEMPLATE_EDITABLE_TYPES.has(notificationType);
}

export function canResendFromActivity(notificationType: string): boolean {
  return ACTIVITY_RESENDABLE_TYPES.has(notificationType);
}

function templateTokens(value: string | null | undefined): string[] {
  if (!value) {
    return [];
  }
  return Array.from(value.matchAll(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g)).map((match) => match[1]);
}

export function validateTemplateOverrides(notificationType: string, overrides: TemplateOverrides): string[] {
  const errors: string[] = [];
  const textEditable = canEditTemplate(notificationType);

  if (!textEditable) {
    if (overrides.subject?.trim()) {
      errors.push('Subject override is not editable for this notification.');
    }
    if (overrides.body?.trim()) {
      errors.push('Body override is not editable for this notification.');
    }
    return errors;
  }

  if ((overrides.subject?.length ?? 0) > 180) {
    errors.push('Subject override must be 180 characters or less.');
  }
  if ((overrides.body?.length ?? 0) > 1200) {
    errors.push('Body override must be 1,200 characters or less.');
  }

  const allowed = new Set(TEMPLATE_OVERRIDE_ALLOWED_TOKENS[notificationType] || []);
  const unknownTokens = new Set([
    ...templateTokens(overrides.subject),
    ...templateTokens(overrides.body)
  ].filter((token) => !allowed.has(token)));

  if (unknownTokens.size > 0) {
    errors.push(`Unknown template token${unknownTokens.size > 1 ? 's' : ''}: ${Array.from(unknownTokens).join(', ')}.`);
  }

  return errors;
}

function isExactBorrowerPairTarget(target: RecipientTarget | undefined): boolean {
  return target?.kind === 'loan_party' && target.party === 'borrower_pair';
}

function isExactEscrowOfficerTarget(target: RecipientTarget | undefined): boolean {
  return target?.kind === 'loan_contact' && target.contact === 'escrow_officer';
}

function isRecipientTargetRecord(target: unknown): target is { kind?: unknown; [key: string]: unknown } {
  return Boolean(target && typeof target === 'object' && !Array.isArray(target));
}

function validateRecipientTarget(
  notificationType: string,
  target: unknown,
  zone: 'to' | 'cc'
): string[] {
  const errors: string[] = [];
  if (!isRecipientTargetRecord(target) || typeof target.kind !== 'string') {
    return [`${zone.toUpperCase()} recipient target must include a supported kind.`];
  }

  const prefix = `${zone.toUpperCase()} ${target.kind}`;

  const supportedKinds = new Set<RecipientTargetKind>([
    'user',
    'user_role',
    'raw_email',
    'loan_role',
    'encompass_loan_role',
    'loan_party',
    'loan_contact'
  ]);
  if (
    !supportedKinds.has(target.kind as RecipientTargetKind)
    || !contractAllowsKind(
      notificationType,
      zone,
      target.kind as RecipientTargetKind
    )
  ) {
    errors.push(`${prefix} is not configurable in ${zone.toUpperCase()} for this notification.`);
  }

  switch (target.kind) {
    case 'user':
      if (typeof target.user_id !== 'string' || !UUID_RE.test(target.user_id)) {
        errors.push('User recipient must reference a valid user id.');
      }
      break;
    case 'user_role':
      if (typeof target.role !== 'string' || !target.role.trim() || target.role.length > 64) {
        errors.push('User role recipient must reference a valid role.');
      }
      break;
    case 'raw_email':
      if (typeof target.email !== 'string' || !EMAIL_RE.test(target.email.trim().toLowerCase())) {
        errors.push('Raw email recipient must be a valid email address.');
      }
      break;
    case 'loan_role':
      if (typeof target.role !== 'string' || !Object.prototype.hasOwnProperty.call(LOAN_ROLE_LABELS, target.role)) {
        errors.push('Loan role recipient is not supported.');
      }
      break;
    case 'encompass_loan_role':
      if (!Number.isInteger(target.role_id) || Number(target.role_id) < 0) {
        errors.push('Encompass loan role recipient must reference a valid numeric role id.');
      }
      if (
        typeof target.role_name_snapshot !== 'string'
        || !target.role_name_snapshot.trim()
        || target.role_name_snapshot.length > 128
      ) {
        errors.push('Encompass loan role recipient must include a role name of 128 characters or less.');
      }
      break;
    case 'loan_party':
      if (typeof target.party !== 'string' || !Object.prototype.hasOwnProperty.call(LOAN_PARTY_LABELS, target.party)) {
        errors.push('Loan party recipient is not supported.');
      }
      break;
    case 'loan_contact':
      if (typeof target.contact !== 'string' || !Object.prototype.hasOwnProperty.call(LOAN_CONTACT_LABELS, target.contact)) {
        errors.push('Loan contact recipient is not supported.');
      }
      break;
    default:
      errors.push(`${prefix} is not supported.`);
  }

  return errors;
}

function recipientTargetKey(target: unknown): string | null {
  if (!isRecipientTargetRecord(target) || typeof target.kind !== 'string') {
    return null;
  }
  switch (target.kind) {
    case 'user':
      return typeof target.user_id === 'string' ? `user:${target.user_id.toLowerCase()}` : null;
    case 'raw_email':
      return typeof target.email === 'string' ? `raw_email:${target.email.trim().toLowerCase()}` : null;
    case 'user_role':
      return typeof target.role === 'string' ? `user_role:${target.role.trim().toLowerCase()}` : null;
    case 'loan_role':
      if (typeof target.role !== 'string') {
        return null;
      }
      return `encompass_loan_role:${
        supportedEncompassRoleForLegacySlug(target.role)?.roleId
          ?? target.role
      }`;
    case 'encompass_loan_role':
      return Number.isInteger(target.role_id)
        ? `encompass_loan_role:${
            supportedEncompassRoleById(Number(target.role_id))?.roleId
              ?? String(target.role_id)
          }`
        : null;
    case 'loan_party':
      return typeof target.party === 'string' ? `loan_party:${target.party}` : null;
    case 'loan_contact':
      return typeof target.contact === 'string' ? `loan_contact:${target.contact}` : null;
    default:
      return null;
  }
}

function duplicateRecipientTargets(config: RecipientConfigV1): string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const target of [...config.to, ...config.cc]) {
    const key = recipientTargetKey(target);
    if (!key) {
      continue;
    }
    if (seen.has(key)) {
      duplicates.add(key);
    }
    seen.add(key);
  }
  return Array.from(duplicates);
}

export function validateRecipientConfig(notificationType: string, config: RecipientConfigV1 | null): string[] {
  const errors: string[] = [];
  if (!config) {
    return errors;
  }
  if (!canEditRecipients(notificationType)) {
    return ['Recipient configuration is not editable for this notification.'];
  }
  if (config.version !== 1 || !Array.isArray(config.to) || !Array.isArray(config.cc)) {
    return ['Recipient configuration must use version 1 with To and CC arrays.'];
  }
  if (config.to.length + config.cc.length > 100) {
    errors.push('Recipient configuration cannot contain more than 100 targets.');
  }
  // The quote alert's worker and database rule carry at most this many CC addresses.
  if (notificationType === 'consumer_quote_failure' && config.cc.length > QUOTE_FAILURE_MAX_CC) {
    errors.push(`Consumer quote failure alerts allow at most ${QUOTE_FAILURE_MAX_CC} CC recipients.`);
  }
  if (duplicateRecipientTargets(config).length > 0) {
    errors.push('Recipient configuration cannot contain duplicate recipients.');
  }

  for (const target of config.to) {
    errors.push(...validateRecipientTarget(notificationType, target, 'to'));
  }
  for (const target of config.cc) {
    errors.push(...validateRecipientTarget(notificationType, target, 'cc'));
  }

  return errors;
}

function uniqueTargets(targets: RecipientTarget[]): RecipientTarget[] {
  const unique: RecipientTarget[] = [];
  for (const target of targets) {
    if (!unique.some((existing) => isSameTarget(existing, target))) {
      unique.push(target);
    }
  }
  return unique;
}

export function normalizeRecipientConfigForType(notificationType: string, config: RecipientConfigV1): RecipientConfigV1 {
  const contract = recipientContractFor(notificationType);
  const lockedFixedEmails = new Set(
    [...(contract?.lockedTo ?? []), ...(contract?.lockedCc ?? [])]
      .map((descriptor) => descriptor.fixedEmail?.trim().toLowerCase())
      .filter((email): email is string => Boolean(email))
      .map((email) => `raw_email:${email}`)
  );
  const isEditableTarget = (target: RecipientTarget) => {
    const key = recipientTargetKey(target);
    return !key || !lockedFixedEmails.has(key);
  };
  const to = config.to.filter((target) => {
    if (!isEditableTarget(target)) {
      return false;
    }
    if (notificationType === 'google_review_request') {
      return !isExactBorrowerPairTarget(target);
    }
    if (notificationType === 'title_escrow_portal_invitation') {
      return !isExactEscrowOfficerTarget(target);
    }
    return true;
  });

  return {
    version: 1,
    to: uniqueTargets(to),
    cc: uniqueTargets(config.cc)
      .filter(isEditableTarget)
      .filter(
        (target) => !to.some((toTarget) => isSameTarget(toTarget, target))
      )
  };
}

function legacyTargets(setting: NotificationSetting, zone: 'to' | 'cc'): RecipientTarget[] {
  const targets: RecipientTarget[] = [];
  const settingsPrimary = SETTINGS_PRIMARY_TYPES.has(setting.notification_type);
  const targetZone = settingsPrimary ? 'to' : 'cc';
  if (zone !== targetZone) {
    return targets;
  }

  for (const userId of setting.additional_recipient_ids ?? []) {
    targets.push({ kind: 'user', user_id: userId });
  }
  for (const email of setting.additional_emails ?? []) {
    if (email?.trim()) {
      targets.push({ kind: 'raw_email', email: email.trim() });
    }
  }
  return targets;
}

export function defaultRecipientConfigFor(setting: NotificationSetting): RecipientConfigV1 {
  const type = setting.notification_type;
  let to: RecipientTarget[] = [];
  const cc: RecipientTarget[] = [];

  if (ADDITIONAL_ONLY_TYPES.has(type)) {
    to = [];
  }

  return {
    version: 1,
    to: [...to, ...legacyTargets(setting, 'to')],
    cc: [...cc, ...legacyTargets(setting, 'cc')]
  };
}

/**
 * Returns the route that will be visible after an explicit reset is saved.
 * Resetting recipient_config also clears both legacy recipient arrays, so the
 * pending UI must not continue rendering recipients that the write removes.
 */
export function codeOwnedRecipientConfigFor(
  setting: NotificationSetting
): RecipientConfigV1 {
  return normalizeRecipientConfigForType(
    setting.notification_type,
    mergeLifecycleDefaultRouting(
      setting.notification_type,
      defaultRecipientConfigFor({
        ...setting,
        recipient_config: null,
        additional_recipient_ids: [],
        additional_emails: []
      })
    )
  );
}

export function effectiveRecipientConfigFor(
  setting: NotificationSetting,
  configOverride?: RecipientConfigV1 | null
): RecipientConfigV1 {
  const storedConfig = isRecipientConfigV1(setting.recipient_config)
    ? setting.recipient_config
    : null;
  const explicitConfig = configOverride === undefined ? storedConfig : configOverride;

  if (explicitConfig) {
    return normalizeRecipientConfigForType(setting.notification_type, explicitConfig);
  }

  return normalizeRecipientConfigForType(
    setting.notification_type,
    mergeLifecycleDefaultRouting(
      setting.notification_type,
      defaultRecipientConfigFor(setting)
    )
  );
}

export function getTargetLabel(target: RecipientTarget, userLabel?: string): string {
  switch (target.kind) {
    case 'user':
      return userLabel ?? 'Selected user';
    case 'user_role':
      return `Role: ${target.role}`;
    case 'raw_email':
      return target.email;
    case 'loan_role':
      return LOAN_ROLE_LABELS[target.role];
    case 'encompass_loan_role':
      return target.role_name_snapshot;
    case 'loan_party':
      return LOAN_PARTY_LABELS[target.party];
    case 'loan_contact':
      return LOAN_CONTACT_LABELS[target.contact];
  }
}
